import React, { useEffect, useState } from 'react';
import {
  History, Trash2, ExternalLink, Search, Loader2,
  FileText, DollarSign, Calendar, Building2,
} from 'lucide-react';
import { getQuotes, deleteQuote } from '../services/quoteService';
import { SavedQuote, QuoteState } from '../types';
import { useUser } from '../contexts/UserContext';

interface Props {
  onLoad: (q: QuoteState) => void;
}

export const MyQuotes: React.FC<Props> = ({ onLoad }) => {
  const { user }             = useUser();
  const [quotes, setQuotes]  = useState<SavedQuote[]>([]);
  const [loading, setLoading]= useState(true);
  const [search,  setSearch] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError]    = useState('');

  const fetchQuotes = async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const qs = await getQuotes(user.uid);
      setQuotes(qs);
    } catch (e) {
      setError('Failed to load quotes. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchQuotes(); }, [user]);

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this quote? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await deleteQuote(id);
      setQuotes(prev => prev.filter(q => q.id !== id));
    } catch { setError('Failed to delete quote.'); }
    finally { setDeleting(null); }
  };

  const filtered = quotes.filter(q =>
    q.customerName.toLowerCase().includes(search.toLowerCase()) ||
    q.companyName?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black text-white">My Quotes</h2>
          <p className="text-gray-500 text-sm">{quotes.length} saved quotes</p>
        </div>
        <button
          onClick={fetchQuotes}
          className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 hover:border-purple-500/30 text-gray-400 hover:text-white text-sm font-medium transition-all"
        >
          Refresh
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-600" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by customer or company..."
          className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-purple-500/50 text-white placeholder-gray-600 text-sm outline-none transition-all"
        />
      </div>

      {error && (
        <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>
      )}

      {/* Quote list */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <Loader2 size={32} className="text-purple-500 animate-spin" />
          <p className="text-gray-600 text-sm">Loading your quotes...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 rounded-2xl bg-white/3 border border-dashed border-white/10">
          <History size={36} className="text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">
            {search ? 'No quotes match your search' : 'No saved quotes yet'}
          </p>
          <p className="text-gray-700 text-sm mt-1">
            {!search && 'Build and save your first quote to see it here.'}
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(q => (
            <div
              key={q.id}
              className="group p-5 rounded-2xl bg-[#0D0D1A] border border-white/8 hover:border-purple-500/30 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-purple-500/10"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500/20 to-violet-500/20 border border-purple-500/20 flex items-center justify-center">
                  <FileText size={16} className="text-purple-400" />
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => { onLoad(q.apiContent); }}
                    title="Load quote"
                    className="p-1.5 rounded-lg text-gray-600 hover:text-purple-400 hover:bg-purple-500/10 transition-all"
                  >
                    <ExternalLink size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(q.id)}
                    disabled={deleting === q.id}
                    title="Delete quote"
                    className="p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-all"
                  >
                    {deleting === q.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  </button>
                </div>
              </div>

              <div className="font-bold text-white text-base mb-0.5 truncate">{q.customerName}</div>
              {q.companyName && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
                  <Building2 size={11} /> {q.companyName}
                </div>
              )}

              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5 text-gray-600">
                    <DollarSign size={13} /> Monthly
                  </span>
                  <span className="font-bold text-purple-400">${q.mrc.toFixed(2)}/mo</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5 text-gray-600">
                    <Calendar size={13} /> Term
                  </span>
                  <span className="text-gray-400">{q.term} months</span>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between">
                <span className="text-[10px] text-gray-700">
                  {q.createdAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
                <button
                  onClick={() => { onLoad(q.apiContent); }}
                  className="text-xs text-purple-500 hover:text-purple-400 font-semibold transition-colors"
                >
                  Load Quote →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
